/**
 * useUpdateUser Hook
 *
 * React Query mutation hook for updating a user's information.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { userService } from '../../../api/user.service';
import type { UserUpdateDTO, UserSanitized } from '../../../types/user.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UpdateUserVariables {
  userId: string;
  data: UserUpdateDTO;
}

/**
 * Hook for updating a user
 */
export const useUpdateUser = (
  tenantId: string
): UseMutationResult<UserSanitized, Error, UpdateUserVariables> => {
  const queryClient = useQueryClient();

  return useMutation<UserSanitized, Error, UpdateUserVariables>({
    mutationFn: ({ userId, data }) => {
      console.log('[useUpdateUser] Updating user:', userId);
      return userService.updateUser(tenantId, userId, data);
    },
    onSuccess: (updatedUser) => {
      console.log('[useUpdateUser] User updated successfully:', updatedUser.userId);
      // Update cache with new user data
      queryClient.setQueryData(['user', tenantId, updatedUser.userId], updatedUser);
      // Invalidate users list to refresh the data
      queryClient.invalidateQueries({ queryKey: ['users', tenantId] });
    },
    onError: (error) => {
      console.error('[useUpdateUser] Failed to update user:', getErrorMessage(error));
    },
  });
};
